import React, { useState } from 'react';
import { Calendar, Clock, User, ArrowRight, Search, Filter } from 'lucide-react';
import { isMobile, isTablet } from '../../utils/deviceUtils';

const blogPosts = [
  {
    id: 1,
    title: "Ultimate Guide to Serengeti Wildlife Migration",
    excerpt: "Discover the best times to witness the Great Migration and where to find the most spectacular wildlife sightings in Tanzania's iconic Serengeti National Park.",
    author: "Sarah Johnson",
    date: "2024-01-15",
    readTime: "8 min read",
    category: "Wildlife",
    image: "https://images.pexels.com/photos/4553618/pexels-photo-4553618.jpeg",
    featured: true,
  },
  {
    id: 2,
    title: "Planning Your First Safari: A Complete Beginner's Guide",
    excerpt: "Everything you need to know about planning your first safari adventure, from choosing the right time to what to pack for your African wildlife experience.",
    author: "David Chen",
    date: "2024-01-12",
    readTime: "12 min read",
    category: "Travel Tips",
    image: "https://images.pexels.com/photos/4577791/pexels-photo-4577791.jpeg",
    featured: false,
  },
  {
    id: 3,
    title: "Ngorongoro Crater: The Eighth Wonder of the World",
    excerpt: "Explore the breathtaking Ngorongoro Crater, home to the highest density of wildlife in Africa and a UNESCO World Heritage Site.",
    author: "Emma Watson",
    date: "2024-01-10",
    readTime: "6 min read",
    category: "Destinations",
    image: "https://images.pexels.com/photos/4577801/pexels-photo-4577801.jpeg",
    featured: false,
  },
  {
    id: 4,
    title: "Photography Tips for Safari: Capturing Wildlife in Action",
    excerpt: "Learn professional photography techniques to capture stunning wildlife moments during your safari adventure.",
    author: "Michael Rodriguez",
    date: "2024-01-08",
    readTime: "10 min read",
    category: "Photography",
    image: "https://images.pexels.com/photos/631317/pexels-photo-631317.jpeg",
    featured: false,
  },
  {
    id: 5,
    title: "Best Time to Visit Tanzania: Seasonal Safari Guide",
    excerpt: "Understand Tanzania's seasons and choose the perfect time for your safari based on wildlife patterns and weather conditions.",
    author: "Lisa Thompson",
    date: "2024-01-05",
    readTime: "7 min read",
    category: "Travel Tips",
    image: "https://images.pexels.com/photos/5669788/pexels-photo-5669788.jpeg",
    featured: false,
  },
  {
    id: 6,
    title: "Cultural Experiences: Meeting Tanzania's Local Communities",
    excerpt: "Go beyond wildlife and discover the rich cultural heritage of Tanzania through authentic local community experiences.",
    author: "James Wilson",
    date: "2024-01-03",
    readTime: "9 min read",
    category: "Culture",
    image: "https://images.pexels.com/photos/12086990/pexels-photo-12086990.jpeg",
    featured: false,
  },
];

const categories = [
  "All",
  "Wildlife",
  "Travel Tips", 
  "Destinations",
  "Photography",
  "Culture",
  "Adventure",
];

export const Blog: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const mobile = isMobile();
  const tablet = isTablet();

  const filteredPosts = blogPosts.filter(post => {
    const matchesCategory = selectedCategory === "All" || post.category === selectedCategory;
    const matchesSearch = post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         post.excerpt.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         post.author.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const featuredPost = blogPosts.find(post => post.featured);
  const regularPosts = filteredPosts.filter(post => !post.featured);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  return (
    <div className="min-h-screen bg-background-primary">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-primary-500 to-primary-600 text-white py-16 sm:py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 p-4 sm:p-6 lg:p-8">
          <div className="text-center">
            <h1 className={`${mobile ? 'text-3xl' : tablet ? 'text-4xl' : 'text-5xl'} font-black mb-4`}>
              Safari Blog
            </h1>
            <p className={`${mobile ? 'text-base' : 'text-lg'} text-white/90 max-w-2xl mx-auto`}>
              Discover insider tips, wildlife insights, and travel stories from our safari experts. 
              Get inspired for your next African adventure.
            </p>
          </div>
        </div>
      </section>

      {/* Search and Filter */}
      <section className="bg-surface-primary border-b border-border-primary py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`flex ${mobile ? 'flex-col gap-4' : 'flex-row gap-6'} items-center justify-between`}>
            {/* Search */}
            <div className={`relative ${mobile ? 'w-full' : 'flex-1 max-w-md'}`}>
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-text-tertiary" />
              <input
                type="text"
                placeholder="Search articles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 rounded-xl border border-border-primary bg-background-primary text-text-primary placeholder-text-tertiary focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            {/* Category Filter */}
            <div className={`flex ${mobile ? 'flex-wrap gap-2' : 'gap-3'} items-center`}>
              <Filter className="w-4 h-4 text-text-tertiary" />
              <div className="flex flex-wrap gap-2">
                {categories.map((category) => (
                  <button
                    key={category}
                    onClick={() => setSelectedCategory(category)}
                    className={`px-3 py-1 rounded-xl text-sm font-medium transition-colors ${
                      selectedCategory === category
                        ? 'bg-primary-500 text-white'
                        : 'bg-background-secondary text-text-secondary hover:bg-background-tertiary'
                    }`}
                  >
                    {category}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Post */}
      {featuredPost && (
        <section className={`${mobile ? 'py-12' : tablet ? 'py-16' : 'py-20'} bg-background-secondary`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className={`${mobile ? 'text-2xl' : 'text-3xl'} font-bold text-text-primary mb-8 text-center`}>
              Featured Article
            </h2>
            <div className="bg-white rounded-xl overflow-hidden shadow-soft hover:shadow-large transition-shadow">
              <div className={`grid ${mobile ? 'grid-cols-1' : 'grid-cols-2'} items-center`}>
                <div className="relative">
                  <img
                    src={featuredPost.image}
                    alt={featuredPost.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="bg-primary-500 text-white px-3 py-1 rounded-xl text-sm font-medium">
                      {featuredPost.category}
                    </span>
                  </div>
                </div>
                <div className="p-6 sm:p-8">
                  <div className="flex items-center gap-4 text-sm text-text-tertiary mb-4">
                    <div className="flex items-center gap-1">
                      <User className="w-4 h-4" />
                      {featuredPost.author}
                    </div>
                    <div className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {formatDate(featuredPost.date)}
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {featuredPost.readTime}
                    </div>
                  </div>
                  <h3 className={`${mobile ? 'text-xl' : 'text-2xl'} font-bold text-text-primary mb-4`}>
                    {featuredPost.title}
                  </h3>
                  <p className="text-text-secondary mb-6 leading-relaxed">
                    {featuredPost.excerpt}
                  </p>
                  <button className="inline-flex items-center gap-2 text-primary-500 font-medium hover:text-primary-600 transition-colors">
                    Read Full Article
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Blog Posts Grid */}
      <section className={`${mobile ? 'py-12' : tablet ? 'py-16' : 'py-20'} bg-background-primary`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8">
            <h2 className={`${mobile ? 'text-2xl' : 'text-3xl'} font-bold text-text-primary`}>
              Latest Articles
            </h2>
            <p className="text-text-secondary text-sm">
              {filteredPosts.length} article{filteredPosts.length !== 1 ? 's' : ''} found
            </p>
          </div>

          {regularPosts.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-text-secondary text-lg">No articles found matching your criteria.</p>
            </div>
          ) : (
            <div className={`grid ${mobile ? 'grid-cols-1 gap-6' : tablet ? 'grid-cols-1 md:grid-cols-2 gap-8' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8'}`}>
              {regularPosts.map((post) => (
                <article key={post.id} className="bg-white rounded-xl overflow-hidden shadow-soft hover:shadow-large transition-shadow">
                  <div className="relative">
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-48 object-cover"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="bg-primary-500 text-white px-3 py-1 rounded-xl text-sm font-medium">
                        {post.category}
                      </span>
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-4 text-sm text-text-tertiary mb-3">
                      <div className="flex items-center gap-1">
                        <User className="w-4 h-4" />
                        {post.author}
                      </div>
                      <div className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {formatDate(post.date)}
                      </div>
                    </div>
                    <h3 className={`${mobile ? 'text-lg' : 'text-xl'} font-bold text-text-primary mb-3 line-clamp-2`}>
                      {post.title}
                    </h3>
                    <p className="text-text-secondary mb-4 leading-relaxed line-clamp-3">
                      {post.excerpt}
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-text-tertiary flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {post.readTime}
                      </span>
                      <button className="inline-flex items-center gap-1 text-primary-500 font-medium hover:text-primary-600 transition-colors text-sm">
                        Read More
                        <ArrowRight className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Newsletter Signup */}
      <section className="bg-gradient-to-r from-primary-500 to-primary-600 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className={`${mobile ? 'text-2xl' : 'text-3xl'} font-bold mb-4`}>
            Stay Updated with Safari Stories
          </h2>
          <p className={`${mobile ? 'text-base' : 'text-lg'} text-white/90 mb-8 max-w-2xl mx-auto`}>
            Subscribe to our newsletter and get the latest safari tips, wildlife updates, 
            and exclusive travel stories delivered to your inbox.
          </p>
          <div className={`flex ${mobile ? 'flex-col' : 'flex-row'} gap-4 max-w-md mx-auto`}>
            <input
              type="email"
              placeholder="Your email address"
              className="flex-1 px-4 py-3 rounded-xl border border-white/30 bg-white/10 text-white placeholder-white/70 focus:outline-none focus:ring-2 focus:ring-white/30"
            />
            <button className="bg-white text-primary-500 px-6 py-3 rounded-xl font-medium hover:bg-gray-100 transition-colors">
              Subscribe
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}; 